import React, { useEffect, useState, useCallback } from "react";
import { Button, ButtonGroup, Card, Col, FormGroup, Input } from "reactstrap";
import { useVsiAppContext } from "../../stores/VsiApp";
import {
  VsiH3,
  VsiH4,
  VsiHr,
  VsiLgText,
  VsiMedLabel,
  VsiMedText,
  VsiSmallText
} from "../../styled/App.styled";
import { useAuthWithRoute, useTitleFromComponent } from "../../utils/hooks";
import { postAutobot } from "../../utils/Verbs";
import AutobotVmwareForm from "../../components/AutobotVmwareForm";
import AutobotLogsModal from "../../components/AutobotLogsModal";
import { returnRootContextWithContext, snakeCaseToTitleCase, toSnakeCase } from "../../utils/Helpers";
import themeStyles from "../../utils/themeStyles";

import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
  useSortable
} from "@dnd-kit/sortable";
import { menuItems } from "../../utils/routes";
import { Icon } from "../../styled/Sidebar.styled";

const activationConstraint = {
  delay: 150, // delay drag by 150ms
  tolerance: 5 // allow 5px movement before activating drag
};

const SortableCategory = ({ id, children, theme, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id, activationConstraint });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    padding: "16px",
    border: "1px solid #ddd",
    borderRadius: ".5rem",
    marginBottom: "8px",
    backgroundColor: themeStyles[theme].cardBgColor
  };

  const returnCategoryIcon = title => {
    const strippedContext = title.toLowerCase().split(" - ")[0];
    const context = returnRootContextWithContext(strippedContext);
    const foundItem = menuItems.find(item => item.context === context);

    return foundItem?.icon ?? "fa-wifi";
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div className={"d-flex flex-row justify-content-between"}>
        <div className={"d-flex flex-row justify-content-start align-items-center"}>
          <Icon className={`fa-solid ${returnCategoryIcon(id)} me-2`} style={{ color: themeStyles[theme].textColor }} />
          <div className={"d-flex flex-row align-items-baseline h-100"}>
            <VsiH3 className={"h-100"}>{id}</VsiH3>
          </div>
        </div>
        <VsiLgText>{index + 1}</VsiLgText>
      </div>
      {children}
    </div>
  );
};

const SortableItem = ({ id, label, theme, index, onItemDelete, onChangeColDef, category }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id, activationConstraint });

  const [activeColDef, setActiveColDef] = useState(label === "Grid" ? category?.userColConfig?.[category?.userColConfig?.length - 1]?._id : null);

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    padding: "8px",
    border: "1px solid #ccc",
    borderRadius: ".5rem",
    marginBottom: "4px",
    backgroundColor: themeStyles[theme].bgColor
  };

  const returnItemIcon = title => {
    const name = title.toLowerCase();

    if (name.includes("grid")) {
      return "fa-table";
    } else if (name.includes("donut") || name.includes("pie")) {
      return "fa-chart-pie";
    } else if (name.includes("alert")) {
      return "fa-monitor-waveform";
    } else if (name.includes("verticalbarchart")) {
      return "fa-chart-column";
    } else if (name.includes("stockchart")) {
      return "fa-chart-line";
    } else if (name.includes("dumbbell")) {
      return "fa-chart-scatter";
    } else if (name.includes("barchart")) {
      return "fa-chart-bar";
    } else if (name.includes("container")) {
      return "fa-chart-tree-map";
    } else if (name.includes("radar")) {
      return "fa-chart-radar";
    } else if (name.includes("areachart")) {
      return "fa-chart-area";
    } else if (name.includes("xrange")) {
      return "fa-calendar-range";
    }

    return "fa-chart-user";
  };

  const changeActiveColDef = (e, defId) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveColDef(defId);
    onChangeColDef(category.path, defId);
  };

  return (
    <div ref={setNodeRef}
         style={{
           ...style,
           overflowX: category?.userColConfig?.length > 5 ? "scroll" : null
         }}
         {...attributes}
         {...listeners}
         className={"d-flex flex-row justify-content-between"}>
      <div className={"d-flex flex-row justify-content-between align-items-center w-100"}>
        <div>
          <Icon className={`fa-solid ${returnItemIcon(label)} me-2`}
                style={{ color: themeStyles[theme].textColor, fontSize: ".8rem" }} />
          <VsiMedText className={"h-100"}>{label}</VsiMedText>
          {label === "Grid" && category?.userColConfig ? (
            category?.userColConfig?.map?.(config => {
              return (
                <Button size={"sm"}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={e => changeActiveColDef(e, config._id)}
                        disabled={activeColDef === config._id}
                        className={"ms-2"}
                        key={`${config.name} button`}>
                  <VsiSmallText><i className={"fa-light fa-table-tree me-1"} />{config.name}</VsiSmallText>
                </Button>
              );
            })
          ) : null}
        </div>
        <div className={"d-flex flex-row align-items-baseline h-100"}>
          <Button size={"sm"}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    if (onItemDelete) {
                      onItemDelete(e, category, label);
                    }
                  }}
                  className={"me-5"}
                  color={"danger"}>
            <i className={"fa-solid fa-x"} />
          </Button>
        </div>
      </div>
      <VsiSmallText>{index + 1}</VsiSmallText>
    </div>
  );
};


const defaultValues = {
  decodingMethod: "greedy",
  maxNewTokens: 5000,
  minNewTokens: 1,
  randomSeed: null,
  stopSequences: null,
  temperature: 1.2,
  topK: 30,
  topP: 1,
  repetitionPenalty: 1
};

const defaultPrompt = `<|system|>
    You are an VMWare admin and have received VMWare Operational data in the HTML table format. Analyze the structured data below and create a summary of each table in about 1000 words or less. Only return the worst 20 instances in the recommendations.

    <|user|>
    expected json response:
{
    "summary": {
        "description": "summary citing causes and impact along with the most common cluster Names or data Center Names of impacted instances.",
        "recommendations": [
            {
                "unitName": "device name",
                "issue": "over or under resource, or resource that has changed"
            }
        ],
        "totalInstancesNeedingAttention": "number",
        "note": "Include a collective summary for each table and provide recommendations for resolving issues."
    }
}`;

const numberValues = ["maxNewTokens", "minNewTokens", "randomSeed", "temperature", "topK", "topP", "repetitionPenalty"];

export default function AutobotConfirmation({ title }) {
  useTitleFromComponent(title);
  const { theme, client, user, savedAutobotPieces, setSavedAutobotPieces, getAccessTokenSilently } = useVsiAppContext();
  const [virtualIncluded, setVirtualIncluded] = useState();
  const [isAdjustModalOpen, setIsAdjustModalOpen] = useState(false);
  const [adjustments, setAdjustments] = useState(defaultValues);
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);

  const [savedPrompt] = useAuthWithRoute(`/api/v6/admin/autobot_prompt/${user.id}`);

  const [adjustedAutobotPieces, setAdjustedAutobotPieces] = useState({});
  const [orderedAutobotPieces, setOrderedAutobotPieces] = useState([]);

  const toggleAdjustModal = () => {
    setIsAdjustModalOpen(!isAdjustModalOpen);
  };

  const setAutobotPiecesAfterAdjustment = (pieces) => {
    const autobotPiecesToUpdate = { ...pieces };
    const configsIds = [];

    (user?.userColConfig ?? []).forEach(config => {
      if (autobotPiecesToUpdate.hasOwnProperty(config.path)) {
        if (!autobotPiecesToUpdate[config.path].hasOwnProperty("userColConfig") || autobotPiecesToUpdate[config.path].userColConfig.find(fig => fig._id === config._id)) {
          autobotPiecesToUpdate[config.path].userColConfig = [];
        }
        if (!configsIds.includes(config._id)) {
          configsIds.push(config._id);
          autobotPiecesToUpdate[config.path].userColConfig.push(config);
        }
      }
    });

    const initialOrderedAutobot = Object.keys(autobotPiecesToUpdate).map(category => {
      const categoryInPlay = autobotPiecesToUpdate[category];

      return {
        ...categoryInPlay,
        id: categoryInPlay.title,
        label: categoryInPlay.title,

        items: Object.keys(categoryInPlay.components ?? {}).map(item => {
          return {
            id: `${categoryInPlay.title}-${item}`,
            label: item,
            ...categoryInPlay.components[item]
          };
        })
      };
    });

    const contextOrder = menuItems.reduce((acc, obj, index) => {
      acc[obj.context] = index;
      return acc;
    }, {});

    initialOrderedAutobot.sort((a, b) => {
      const strippedContextA = a.title.toLowerCase().split(" - ")[0];
      const contextA = snakeCaseToTitleCase(returnRootContextWithContext(strippedContextA));

      const strippedContextB = b.title.toLowerCase().split(" - ")[0];
      const contextB = snakeCaseToTitleCase(returnRootContextWithContext(strippedContextB));

      const indexA = contextOrder[contextA] ?? Infinity; // Use Infinity if not found
      const indexB = contextOrder[contextB] ?? Infinity; // Use Infinity if not found
      return indexA - indexB;
    });

    setAdjustedAutobotPieces(autobotPiecesToUpdate);
    setOrderedAutobotPieces(initialOrderedAutobot);
  };

  useEffect(() => {
    setAutobotPiecesAfterAdjustment(savedAutobotPieces);
  }, [savedAutobotPieces, user]);

  useEffect(() => {
    if (savedPrompt.length) {
      let { prompt, ...otherAdjustments } = savedPrompt[0];
      setPrompt(prompt);
      setAdjustments(otherAdjustments);
    }

    if (adjustedAutobotPieces["virtual_observations_section"]) {
      setVirtualIncluded(true);
    } else {
      setVirtualIncluded(false);
    }
  }, [adjustedAutobotPieces, savedPrompt]);

  const toggleLogsModal = () => {
    setIsLogsModalOpen(!isLogsModalOpen);
  };

  const sendAutobotEmail = async (event) => {
    event.preventDefault();
    event.target.innerHTML = "Running";
    event.target.disabled = true;

    let userToken = await getAccessTokenSilently();
    let snakeAdjustments = {};

    Object.keys(adjustments).forEach(function(key, index) {
      if (numberValues.includes(key)) {
        snakeAdjustments[toSnakeCase(key)] = parseFloat(adjustments[key]) || null;
      } else if (key === "stopSequences") {
        snakeAdjustments[toSnakeCase(key)] = adjustments[key]?.split(",")?.map(adjustment => adjustment.trim()) || [];
      } else {
        snakeAdjustments[toSnakeCase(key)] = adjustments[key];
      }
    });

    let autobotPieces = {
      ...adjustedAutobotPieces,
      order: orderedAutobotPieces,
      vmware_parameters: snakeAdjustments,
      vmware_prompt: prompt || defaultPrompt
    };

    try {
      await postAutobot(`/api/v6/autobot/client/${client.clientId}`, autobotPieces, userToken);
      event.target.innerHTML = "Rerun";
      event.target.disabled = false;
    } catch (error) {
      console.error("Autobot error");
      event.target.innerHTML = "Rerun - Error";
      event.target.disabled = false;
    }

  };

  const onValueChecked = (e) => {
    setVirtualIncluded(e.target.checked);
    if (e.target.checked) {
      // TODO: Save autobot components
      setSavedAutobotPieces({
        ...adjustedAutobotPieces,
        "virtual_observations_section": { "included": "VmwareObservations" }
      });
    } else {
      // TODO: Save autobot components
      setSavedAutobotPieces(Object.keys(adjustedAutobotPieces).reduce((newObj, key) => {
        if (key !== "virtual_observations_section") {
          newObj[key] = adjustedAutobotPieces[key];
        }
        return newObj;
      }, {}));
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleCategoryDragEnd = ({ active, over }) => {
    if (active.id && over?.id && active.id !== over.id) {
      setOrderedAutobotPieces((prev) =>
        arrayMove(prev, prev.findIndex((cat) => cat.id === active.id), prev.findIndex((cat) => cat.id === over.id))
      );
    }
  };

  const handleItemDragEnd = (categoryId) => ({ active, over }) => {
    if (!over) return; // Drop outside a valid area
    if (active.id !== over.id) {
      setOrderedAutobotPieces((prevCategories) =>
        prevCategories.map((category) =>
          category.id === categoryId
            ? {
              ...category,
              items: arrayMove(
                category.items,
                category.items.findIndex((item) => item.id === active.id),
                category.items.findIndex((item) => item.id === over.id)
              )
            }
            : category
        )
      );
    }
  };

  const onItemDelete = (e, category, id) => {
    e.preventDefault();
    e.stopPropagation();

    const path = category.path;
    const autobotPiecesToUpdate = { ...savedAutobotPieces };
    if (autobotPiecesToUpdate[path]) {
      try {
        delete autobotPiecesToUpdate[path].components[id];
      } catch (error) {
        console.error(error);
      }
    }

    if (autobotPiecesToUpdate[path] && Object.keys(autobotPiecesToUpdate[path].components).length === 0) {
      try {
        delete autobotPiecesToUpdate[path];
      } catch (error) {
        console.error(error);
      }
    }

    setAutobotPiecesAfterAdjustment(autobotPiecesToUpdate);

  };

  const onChangeColDef = useCallback((path, defId) => {

    const autobotPiecesToUpdate = { ...savedAutobotPieces };
    const foundDefs = savedAutobotPieces[path].userColConfig.find(fig => fig._id === defId);
    if (foundDefs) {
      autobotPiecesToUpdate[path].components["Grid"].savedColConfig = autobotPiecesToUpdate[path].components["Grid"].savedColConfig.map(piece => {
        const foundDef = foundDefs?.colState?.find?.(col => (col?.colId ?? col?.field) === (piece?.colId ?? piece.field)) ?? {};
        return {
          ...piece,
          ...foundDef
        };
      });
      setAutobotPiecesAfterAdjustment(autobotPiecesToUpdate);
    }

  }, [savedAutobotPieces]);


  return (
    <Card className={`report-card p-2 nav-card-${theme}`}>
      <VsiH4>Custom Email Confirmation</VsiH4>
      <VsiHr />
      {user.role === "super_user" ? (<div>
        <Col className={"d-flex flex-row mb-4"}>
          <ButtonGroup>
            <Button
              name="autobotLogs"
              id="autobotLogsBtn"
              color="secondary"
              size="sm"
              onClick={toggleLogsModal}
            >
              View Logs
            </Button>
          </ButtonGroup>
        </Col>
        <Col>
          <FormGroup className="mb-3" check>
            <Input type="checkbox"
                   name="isEnabled"
                   checked={virtualIncluded}
                   onChange={onValueChecked} />
            {" "}
            <VsiMedLabel check>
              Include VMWare Operational report?
            </VsiMedLabel>
          </FormGroup>
        </Col>
        {virtualIncluded ?
          <Col className={"d-flex flex-row mb-4"}>
            <ButtonGroup>
              <Button
                name="adjustOutput"
                id="adjustOutputBtn"
                color="secondary"
                size="sm"
                onClick={toggleAdjustModal}
              >
                Adjust AI Summary Output
              </Button>
            </ButtonGroup>
          </Col>
          : null}

        <AutobotVmwareForm
          isAdjustModalOpen={isAdjustModalOpen}
          toggleAdjustModal={toggleAdjustModal}
          adjustments={adjustments}
          setAdjustments={setAdjustments}
          defaultValues={defaultValues}
          prompt={prompt}
          setPrompt={setPrompt}
          defaultPrompt={defaultPrompt}
          savedAdjustments={savedPrompt?.[0] || { ...defaultValues, prompt: defaultPrompt }} />
        <AutobotLogsModal
          isLogsModalOpen={isLogsModalOpen}
          toggleLogsModal={toggleLogsModal} />
      </div>) : null}
      {orderedAutobotPieces.length ?
        <Col className={"d-flex flex-row mb-4 align-items-center"}>
          <ButtonGroup>
            <Button
              name="sendEmail"
              id="sendEmailBtn"
              color="success"
              onClick={sendAutobotEmail}
            >
              Send Custom Email
            </Button>
          </ButtonGroup>
          <VsiLgText className={"ms-2"}>* Adjust order by sorting through drag and drop</VsiLgText>
        </Col>
        : null}
      <DndContext sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleCategoryDragEnd}>
        <SortableContext items={orderedAutobotPieces.map((cat) => cat.id)}
                         strategy={verticalListSortingStrategy}>
          {orderedAutobotPieces.map((category, catIndex) => category.items.length > 0 ? (
            <SortableCategory key={category.id}
                              index={catIndex}
                              id={category.id}
                              theme={theme}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleItemDragEnd(category.id)}
              >
                <SortableContext items={category.items.map((item) => item.id)}>
                  {category.items.map((item, index) => (
                    <SortableItem key={item.id}
                                  category={category}
                                  onItemDelete={onItemDelete}
                                  index={index}
                                  onChangeColDef={onChangeColDef}
                                  id={item.id}
                                  label={item.label}
                                  theme={theme} />
                  ))}
                </SortableContext>
              </DndContext>
            </SortableCategory>
          ) : null)}
        </SortableContext>
      </DndContext>
    </Card>
  );
};
